import React from 'react';
import '../../styles/list-glass.css';
import './invoiceTemplate-view.css';
import { Redirect } from "react-router-dom";
import SpinnerView from '../../components/spinner/spinner-view';
import hb from 'handlebars';
import moment from 'moment';

// Helpers — mirror server/gen.js so the live preview renders exactly like the PDF.
const fmtCurrency = (n) => {
  const v = parseFloat(n);
  if (isNaN(v)) return '0.00';
  return v.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
hb.registerHelper('currency', fmtCurrency);
hb.registerHelper('dateFormat', function (date, options) {
  const f = (options && options.hash && options.hash.format) || 'YYYY-MM-DD';
  return moment(date).format(f);
});
hb.registerHelper('secToMin', function (num) {
  const s = parseInt(num, 10) || 0;
  return new Date(s * 1000).toISOString().substr(11, 8);
});

// Realistic sample data so the preview looks like a real invoice.
const SAMPLE = {
  invoiceNumber: 'INV00042', quoteNumber: 'QUO00042', isQuote: false,
  invoiceDate: new Date(), dueDate: moment().add(30, 'days').toDate(),
  to: 'ABC Farm (Pty) Ltd', notes: 'Thank you for your business.',
  sub_total: '1650.00', discount_total: '0.00', tax: '247.50', tax2: '0.00', total_price: '1897.50',
  discount: { hasDiscount: false },
  descriptions: [
    { description: 'Call-out fee', code: 'CALL', qty: 1, price: '350.00', total: '350.00' },
    { description: 'Labour (2h @ R650)', code: 'LAB', qty: 2, price: '650.00', total: '1300.00' },
  ],
  company: {
    companyName: 'Your Company', EmailAddress: 'accounts@yourcompany.co.za', contactNumber: '+27 11 555 0000',
    addressLine1: '1 Main Road', addressLine2: 'Suite 4', city: 'Cape Town', postalCode: '8001', website: 'www.yourcompany.co.za',
    BankName: 'FNB', BankBranch: '250655', BankAccountType: 'Cheque', BankAccountNumber: '123456789',
    paymentTerms: '30 days', companyLogo: null,
    tax: { hasTax: true, tD: 'VAT', hasSecondTax: false, t2D: '' },
    companyColors: { primary: '#0DA1B2', secondary: '#067886', background: '#ffffff', action: '#0DA1B2' },
  },
  client: {
    name: 'ABC Farm', surname: '', email: 'accounts@abcfarm.co.za', contactNumber: '+27 21 555 1234',
    addressLine1: '12 Farm Road', addressLine2: '', suburb: 'Wellington', city: 'Paarl', postalCode: '7646',
    vat: '4123456789', regNumber: '2019/123456/07',
  },
  calendar: { clientName: 'ABC Farm', appointmentType: 'Site Survey', startDate: new Date() },
};

// The palette of insertable variables / helpers, grouped.
const FIELD_GROUPS = [
  { group: 'Invoice', fields: [
    { label: 'Invoice #', token: '{{invoiceNumber}}' },
    { label: 'Quote #', token: '{{quoteNumber}}' },
    { label: 'Invoice date', token: '{{dateFormat invoiceDate format="DD MMM YYYY"}}' },
    { label: 'Due date', token: '{{dateFormat dueDate format="DD MMM YYYY"}}' },
    { label: 'Billed to', token: '{{to}}' },
    { label: 'Notes', token: '{{notes}}' },
    { label: 'Subtotal', token: 'R {{currency sub_total}}' },
    { label: 'Discount', token: 'R {{currency discount_total}}' },
    { label: 'Tax', token: 'R {{currency tax}}' },
    { label: 'Total', token: 'R {{currency total_price}}' },
  ] },
  { group: 'Company', fields: [
    { label: 'Name', token: '{{company.companyName}}' },
    { label: 'Logo', token: '{{#if company.companyLogo}}<img src="https://app.moneyapp.online/api/images/{{company.companyLogo}}" style="max-height:70px">{{/if}}' },
    { label: 'Email', token: '{{company.EmailAddress}}' },
    { label: 'Phone', token: '{{company.contactNumber}}' },
    { label: 'Address', token: '{{company.addressLine1}}, {{company.city}} {{company.postalCode}}' },
    { label: 'Website', token: '{{company.website}}' },
    { label: 'Bank', token: '{{company.BankName}} — {{company.BankAccountNumber}} (Branch {{company.BankBranch}})' },
    { label: 'Tax label', token: '{{company.tax.tD}}' },
  ] },
  { group: 'Client', fields: [
    { label: 'Name', token: '{{client.name}} {{client.surname}}' },
    { label: 'Email', token: '{{client.email}}' },
    { label: 'Phone', token: '{{client.contactNumber}}' },
    { label: 'Address', token: '{{client.addressLine1}}, {{client.city}}' },
    { label: 'VAT no', token: '{{client.vat}}' },
    { label: 'Reg no', token: '{{client.regNumber}}' },
  ] },
  { group: 'Line items', fields: [
    { label: 'Line-items loop', token: '{{#each descriptions}}\n  <tr>\n    <td>{{this.description}}</td>\n    <td>{{this.qty}}</td>\n    <td>R {{currency this.price}}</td>\n    <td>R {{currency this.total}}</td>\n  </tr>\n{{/each}}' },
  ] },
  { group: 'Helpers', fields: [
    { label: 'Currency', token: '{{currency VALUE}}' },
    { label: 'Date format', token: '{{dateFormat VALUE format="DD MMM YYYY"}}' },
    { label: 'If / else', token: '{{#if VALUE}}\n  \n{{else}}\n  \n{{/if}}' },
  ] },
];

class HomeView extends React.Component {
  state = {
    invoiceTemplate: {}, previewHtml: '', previewError: '',
    // AI edit drawer
    chatOpen: false, chatMsgs: [], chatInput: '', chatBusy: false, undoStack: [], pendingImages: {},
  };
  templateRef = React.createRef();
  chatEndRef = React.createRef();
  fileInputRef = React.createRef();

  componentDidMount() {
    this.props.getInvoiceTemplate(this.props.match.params.id);
  }

  componentWillUpdate(props, state) {
    if (props.invoiceTemplate && state.invoiceTemplate) {
      if (state.invoiceTemplate._id != props.invoiceTemplate._id) {
        this.setState({ invoiceTemplate: props.invoiceTemplate }, () => this.compile(props.invoiceTemplate.template));
      }
    }
    return true;
  }

  compile = (templateStr) => {
    if (!templateStr) { this.setState({ previewHtml: '', previewError: '' }); return; }
    try {
      // Merge the REAL company over the sample so the preview shows the
      // actual logo/colours/bank details instead of placeholders.
      const data = this.props.company
        ? { ...SAMPLE, company: { ...SAMPLE.company, ...this.props.company } }
        : SAMPLE;
      let html = hb.compile(templateStr, { strict: false })(data);
      // Logo tokens point at the production images URL — rewrite to this
      // deployment's own API so the image loads on localhost too.
      if (data.company && data.company.companyLogo) {
        const urls = require('../../network/urls').default;
        html = html.split('https://app.moneyapp.online/api/images/').join(urls.getURL() + '/api/images/');
      }
      this.setState({ previewHtml: html.replace(/(<script)/gi, 'illegalscript'), previewError: '' });
    } catch (e) {
      this.setState({ previewError: e.message });
    }
  };

  handleChange = (value, prop) => {
    const t = { ...this.state.invoiceTemplate, [prop]: value };
    this.setState({ invoiceTemplate: t });
    if (prop === 'template') this.compile(value);
  };

  insertToken = (token) => {
    const el = this.templateRef.current;
    const current = this.state.invoiceTemplate.template || '';
    let next;
    if (el && typeof el.selectionStart === 'number') {
      const s = el.selectionStart, e = el.selectionEnd;
      next = current.slice(0, s) + token + current.slice(e);
    } else {
      next = current + token;
    }
    this.handleChange(next, 'template');
    // restore focus after the value updates
    setTimeout(() => { if (el) { el.focus(); } }, 0);
  };

  save = () => this.props.saveInvoiceTemplate(this.state.invoiceTemplate);

  saveAsNew = async () => {
    const current = this.state.invoiceTemplate;
    const name = window.prompt(
      'Name for the new template (e.g. "Invoice — Corporate clients"):',
      (current.invoiceTemplateName || 'Invoice') + ' (copy)'
    );
    if (!name || !name.trim()) return;
    const res = await this.props.saveInvoiceTemplateAsNew(current, name.trim());
    if (res && res.id) {
      // Jump to the new template so further edits (and Save) hit the copy,
      // leaving the original untouched.
      this.props.history.push('/invoiceTemplate/' + res.id);
      this.props.getInvoiceTemplate(res.id);
    }
  };

  // ── AI edit drawer ────────────────────────────────────────────────────

  scrollChat = () => {
    setTimeout(() => this.chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  };

  attachImage = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const token = `__IMG_${Date.now().toString(36)}__`;
      this.setState({
        pendingImages: { ...this.state.pendingImages, [token]: reader.result },
        chatInput: (this.state.chatInput ? this.state.chatInput + ' ' : '') + `Add this image (${token}) `,
      });
    };
    reader.readAsDataURL(file);
  };

  sendChat = async () => {
    const message = this.state.chatInput.trim();
    if (!message || this.state.chatBusy) return;

    const msgs = [...this.state.chatMsgs, { role: 'user', text: message }];
    this.setState({ chatMsgs: msgs, chatInput: '', chatBusy: true });
    this.scrollChat();

    try {
      const res = await this.props.aiEditTemplate({
        message,
        template: this.state.invoiceTemplate.template || '',
        templateName: this.state.invoiceTemplate.invoiceTemplateName || 'Invoice',
        history: msgs.slice(-8),
        images: this.state.pendingImages,
      });
      this.setState({ pendingImages: {} });

      if (res && res.reply) {
        const next = { chatMsgs: [...msgs, { role: 'assistant', text: res.reply }], chatBusy: false };
        if (res.template) {
          // Snapshot for undo, then apply — the live preview recompiles instantly.
          next.undoStack = [...this.state.undoStack, this.state.invoiceTemplate.template || ''];
          this.setState(next, () => this.handleChange(res.template, 'template'));
        } else {
          this.setState(next);
        }
      } else {
        this.setState({
          chatMsgs: [...msgs, { role: 'assistant', text: (res && res.error) || 'Something went wrong — please try again.' }],
          chatBusy: false,
        });
      }
    } catch (e) {
      this.setState({
        chatMsgs: [...msgs, { role: 'assistant', text: 'Could not reach the assistant. Please try again.' }],
        chatBusy: false,
      });
    }
    this.scrollChat();
  };

  undoAi = () => {
    const stack = [...this.state.undoStack];
    if (!stack.length) return;
    const prev = stack.pop();
    this.setState({ undoStack: stack }, () => this.handleChange(prev, 'template'));
  };

  render() {
    if (this.props.user && !this.props.user._id) return <Redirect to='/' />;
    const t = this.state.invoiceTemplate || {};
    const inputStyle = { width: '100%', padding: '10px 13px', border: '1px solid rgba(15,31,46,0.12)', borderRadius: 10, fontSize: 14, outline: 'none', background: 'rgba(255,255,255,0.7)' };

    return (
      <div className="content-wrapper" style={{ padding: '0 14px 24px' }}>
        <SpinnerView showSpinner={this.props.loading} />

        <div className="gl-hero" style={{ margin: '0 0 16px' }}>
          <div className="gl-hero-row">
            <div>
              <div className="gl-hero-title">Invoice template</div>
              <div className="gl-hero-sub">Design the invoice/quote PDF — click a field to insert it, preview updates live.</div>
            </div>
            <div className="gl-hero-actions">
              <button type="button" className="gl-hero-btn" onClick={() => this.setState({ chatOpen: !this.state.chatOpen })}>
                <i className="fas fa-magic" /> Edit with AI
              </button>
              <button type="button" className="gl-hero-btn" onClick={this.saveAsNew} title="Fork this design into a new template — the original stays as-is">
                <i className="fas fa-copy" /> Save as new
              </button>
              <button type="button" className="gl-hero-btn solid" onClick={this.save}><i className="fas fa-save" /> Save</button>
            </div>
          </div>
        </div>

        <div className="itpl-grid">
          {/* LEFT — editor + palette */}
          <div className="itpl-edit">
            <label className="itpl-lbl">Template name</label>
            <input type="text" style={inputStyle} value={t.invoiceTemplateName || ''} onChange={(e) => this.handleChange(e.target.value, 'invoiceTemplateName')} />

            <div className="itpl-row2">
              <div>
                <label className="itpl-lbl">Email subject</label>
                <input type="text" style={inputStyle} value={t.invoiceTemplateEmailSubject || ''} onChange={(e) => this.handleChange(e.target.value, 'invoiceTemplateEmailSubject')} placeholder="Invoice" />
              </div>
            </div>

            <label className="itpl-lbl">Email body</label>
            <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} value={t.invoiceTemplateEmailBody || ''} onChange={(e) => this.handleChange(e.target.value, 'invoiceTemplateEmailBody')} />

            <label className="itpl-lbl">Template (HTML)</label>
            <div className="itpl-palette">
              {FIELD_GROUPS.map((g) => (
                <div key={g.group} className="itpl-pal-group">
                  <span className="itpl-pal-grouplbl">{g.group}</span>
                  {g.fields.map((f) => (
                    <button key={f.label} type="button" className="itpl-chip" title={f.token} onClick={() => this.insertToken(f.token)}>{f.label}</button>
                  ))}
                </div>
              ))}
            </div>
            <textarea ref={this.templateRef} className="itpl-code" value={t.template || ''} onChange={(e) => this.handleChange(e.target.value, 'template')} spellCheck={false} />
          </div>

          {/* AI edit drawer */}
          {this.state.chatOpen && (
            <div className="itpl-ai">
              <div className="itpl-ai-head">
                <span><i className="fas fa-magic" /> Template assistant</span>
                <div>
                  {this.state.undoStack.length > 0 && (
                    <button type="button" className="itpl-ai-undo" onClick={this.undoAi} title="Undo last AI change">
                      <i className="fas fa-undo" /> Undo
                    </button>
                  )}
                  <button type="button" className="itpl-ai-close" onClick={() => this.setState({ chatOpen: false })}>
                    <i className="fas fa-times" />
                  </button>
                </div>
              </div>

              <div className="itpl-ai-msgs">
                {this.state.chatMsgs.length === 0 && (
                  <div className="itpl-ai-hint">
                    Tell me what to change — for example:
                    <ul>
                      <li>"Make the header teal with the company logo on the right"</li>
                      <li>"Add the client's VAT number under their address"</li>
                      <li>"Add a 'Thank you for your business' footer"</li>
                      <li>"Use this image as a banner: https://…"</li>
                    </ul>
                    Changes show in the preview instantly — click <b>Save</b> when you're happy, or <b>Undo</b> to step back.
                  </div>
                )}
                {this.state.chatMsgs.map((m, i) => (
                  <div key={i} className={`itpl-ai-msg ${m.role}`}>{m.text}</div>
                ))}
                {this.state.chatBusy && <div className="itpl-ai-msg assistant busy">Working on it…</div>}
                <div ref={this.chatEndRef} />
              </div>

              <div className="itpl-ai-inputrow">
                <input type="file" accept="image/*" ref={this.fileInputRef} style={{ display: 'none' }} onChange={this.attachImage} />
                <button type="button" className="itpl-ai-attach" title="Attach an image" onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}>
                  <i className="fas fa-image" />
                </button>
                <textarea
                  className="itpl-ai-input"
                  placeholder="Describe the change…"
                  value={this.state.chatInput}
                  rows={2}
                  onChange={(e) => this.setState({ chatInput: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.sendChat(); } }}
                />
                <button type="button" className="itpl-ai-send" disabled={this.state.chatBusy || !this.state.chatInput.trim()} onClick={this.sendChat}>
                  <i className="fas fa-paper-plane" />
                </button>
              </div>
            </div>
          )}

          {/* RIGHT — live preview */}
          <div className="itpl-preview-wrap">
            <div className="itpl-lbl" style={{ marginTop: 0 }}>Live preview <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>(sample data)</span></div>
            {this.state.previewError
              ? <div className="itpl-err">Template error: {this.state.previewError}</div>
              : <div className="itpl-preview" dangerouslySetInnerHTML={{ __html: this.state.previewHtml || '<div style="color:#8a99a8;padding:24px;text-align:center">Start typing or insert fields — your invoice renders here.</div>' }} />}
          </div>
        </div>
      </div>
    );
  }
}

export default HomeView;
